import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ProgressModal from "../../components/modals/ProgressModal";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ImportMediaCsvDropzone from "../../components/importMedia/ImportMediaCsvDropzone";
import subDays from "date-fns/subDays";

export function ManageShippingsViewModel(logout, addSnackbar) {
  const [filters, setFilters] = useState({
    customer: { value: -1, label: "All Customers" },
    startDate: null,
    endDate: null,
  });

  const [showDates, setShowDates] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [sunOrdersLoading, setSunOrdersLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(null);
  const [shippings, setShippings] = useState({ data: [] });
  const [sunOrders, setSunOrders] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getShippings = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall("fetchShippings", payload);

    if (callResult.success) {
      setShippings(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const getShippingSunOrders = async function (search, filterArg) {
    setSunOrdersLoading(true);
    const payload = {
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall("fetchShippingSunOrders", payload);

    if (callResult.success) {
      setSunOrders(callResult.data?.sun_orders ?? []);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setSunOrdersLoading(false);
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makePostApiCall(`getCustomersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const resetFilters = () => {
    const filterReset = {
      customer: { value: -1, label: "All Customers" },
      startDate: null,
      endDate: null,
    };
    setFilters(filterReset);

    setShowFilters(false);
    getShippings(null, 1, filterReset);
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="Customers"
      onChange={(value) =>
        setFilters({
          ...filters,
          customer: value,
        })
      }
      options={customers}
      resetFilter={() =>
        setFilters({
          ...filters,
          customer: { value: -1, label: "All Customers" },
        })
      }
      valueName="customer"
      value={filters.customer}
    />,
  ];
  const deleteManufacturedProduct = async function (id) {
    let callResult = await makePostApiCall(`shippings/delete/${id}`);

    if (callResult.success) {
      const filtereditems = shippings.data.filter((item) => item.id !== id);
      setShippings({ ...shippings, data: filtereditems });

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeleteShipping = function (shipping) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteManufacturedProduct(shipping.id)}
          message={`Are you sure you want to delete shipping for ${shipping.customer}, PO #: ${shipping.po_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const uploadSpsCsvForSun = async function (uploadedFile) {
    showProgressModal("Importing SPS orders...");
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    const callResult = await makePostApiCall(
      "importSpsForForSun",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message ?? "SPS orders imported",
          SnackbarType.Success
        )
      );
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      await getShippingSunOrders(null, filters);
      hideProgressModal();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      hideProgressModal();
      showErrorModal(
        callResult.data?.message ?? "Could not import SPS orders"
      );
    }
  };

  const openImportSpsForSun = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="spsCsv"
          actionCall={uploadSpsCsvForSun}
          setOpen={setEmptyModelOpen}
          title="Import SPS"
        />
      ),
      open: true,
    });
  };

  const createMultipleSunInvoices = async function () {
    const shipmentIds = (selectedProducts ?? [])
      .filter((item) => item.record_type === "shipping")
      .map((item) => item.id);

    if (shipmentIds.length === 0) {
      showErrorModal("Please select at least one shipment.");
      return;
    }

    showProgressModal("Creating invoices...");
    const callResult = await makePostApiCall("sun/create-multiple-invoices", {
      shipments: shipmentIds,
    });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message ?? "Invoices created",
          SnackbarType.Success
        )
      );
      setSelectedProducts(null);
      await getShippings(null, 1, filters);
      hideProgressModal();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      hideProgressModal();
      showErrorModal(
        callResult.data?.message ?? "Could not create invoices"
      );
    }
  };

  return {
    getShippings,
    getShippingSunOrders,
    loading,
    sunOrdersLoading,
    shippings,
    sunOrders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    setShowFilters,
    showFilters,
    resetFilters,
    getCustomersForDropdown,
    filterComponents,
    filters,
    askDeleteShipping,
    openImportSpsForSun,
    createMultipleSunInvoices,
    selectedProducts,
    setSelectedProducts,
    setFilters,
    setShowDates,
    showDates,
  };
}
